import React from 'react';
import SectionCard, { SectionEmpty } from './SectionCard';
import { RUN_OUTCOME, fmtCount, fmtPct, fmtRelative } from './format';

const SUITES_SHOWN = 8;

function CoverageBar({ automation }) {
  const total = automation.eligible || 0;
  if (!total) return null;
  const pct = (n) => `${(n / total) * 100}%`;
  return (
    <div className="pqd-coverage">
      <div
        className="pqd-progress pqd-progress--lg"
        role="img"
        aria-label={`${automation.automated} automated, ${automation.script_pending} script pending, ${automation.not_automated} not automated`}
      >
        <span className="pqd-progress__seg pqd-progress__seg--passed" style={{ width: pct(automation.automated) }} />
        <span className="pqd-progress__seg pqd-progress__seg--pending" style={{ width: pct(automation.script_pending) }} />
      </div>
      <ul className="pqd-legend">
        <li><span className="pqd-legend__dot pqd-legend__dot--passed" />Automated <strong>{fmtCount(automation.automated)}</strong></li>
        <li><span className="pqd-legend__dot pqd-legend__dot--pending" />Script pending <strong>{fmtCount(automation.script_pending)}</strong></li>
        <li><span className="pqd-legend__dot" />Not automated <strong>{fmtCount(automation.not_automated)}</strong></li>
      </ul>
    </div>
  );
}

export default function SuiteHealth({ section, navigateTo, projectId }) {
  const { data, loading, error, retry, refreshing } = section;
  const automation = data?.automation;
  const suites = data?.suites;

  return (
    <SectionCard
      id="pqd-suite-health"
      title="Automation coverage & suite health"
      subtitle="Current snapshot across all environments; suite runs follow the environment filter."
      loading={loading}
      error={error}
      onRetry={retry}
      refreshing={refreshing}
      skeletonLines={6}
      actions={
        <button type="button" className="rt2-link" onClick={() => navigateTo('test-suites')}>
          All suites
        </button>
      }
    >
      {automation && (
        <>
          <div className="pqd-coverage__head">
            <span className="pqd-big">{fmtPct(automation.coverage)}</span>
            <span className="pqd-muted">
              {automation.eligible ? `${fmtCount(automation.automated)} of ${fmtCount(automation.eligible)} active tests automated` : 'No active test cases'}
            </span>
          </div>
          <CoverageBar automation={automation} />

          <dl className="pqd-mini-stats">
            <div className="pqd-mini-stat">
              <dt title="Active tests with no recorded execution in any environment">Never executed</dt>
              <dd>{fmtCount(automation.never_executed)}</dd>
            </div>
            <div className="pqd-mini-stat">
              <dt title={`Executed before, but not in the last ${automation.stale_after_days} days`}>
                Not run in {automation.stale_after_days}d
              </dt>
              <dd>{fmtCount(automation.stale)}</dd>
            </div>
            <div className="pqd-mini-stat">
              <dt>Self-healed</dt>
              <dd>{fmtCount(automation.self_healed)}</dd>
            </div>
          </dl>

          <h3 className="pqd-subheading">
            Suites {suites?.total ? <span className="pqd-muted">({fmtCount(suites.total)})</span> : null}
          </h3>
          {!suites?.items?.length ? (
            <SectionEmpty
              title="No test suites yet"
              action={
                <button type="button" className="rt-btn rt-btn--secondary rt-btn--sm" onClick={() => navigateTo('test-suites')}>
                  Create a suite
                </button>
              }
            />
          ) : (
            <ul className="pqd-suites">
              {suites.items.slice(0, SUITES_SHOWN).map((suite) => {
                const health = RUN_OUTCOME[suite.health] || RUN_OUTCOME.unknown;
                return (
                  <li key={suite.suite_id}>
                    <button
                      type="button"
                      className="pqd-suite"
                      onClick={() => navigateTo(`test-suites/${suite.suite_id}?project_id=${projectId}`)}
                    >
                      <span className={`rt-status-dot rt-status-dot--${health.tone}`} aria-hidden="true" />
                      <span className="pqd-suite__name">{suite.name}</span>
                      <span className="pqd-suite__meta">
                        {fmtCount(suite.test_cases)} tests ·{' '}
                        {suite.last_run ? `${health.label} ${fmtRelative(suite.last_run.at)}` : 'Never run'}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {suites?.total > SUITES_SHOWN && (
            <p className="pqd-footnote">Showing the {SUITES_SHOWN} suites needing the most attention.</p>
          )}
        </>
      )}
    </SectionCard>
  );
}
